import { useEffect } from "react"
import { useQueryClient } from "@tanstack/react-query"
import type { RealtimeChannel, SupabaseClient } from "@supabase/supabase-js"

// Subscribes to Supabase Realtime postgres_changes for a table and invalidates
// the given React Query key. The subscription only signals "something changed" —
// the actual re-fetch goes through authenticated tRPC, so no auth leak here.
export function useRealtimeInvalidate(table: string, queryKey: readonly unknown[], enabled = true) {
  const queryClient = useQueryClient()
  const key = queryKey.join(`,`)

  useEffect(() => {
    if (!enabled) return
    // Lazy-import the Supabase browser client so its ~53 KiB bundle stays out of
    // the initial (critical-path) JS. Realtime is a post-load concern, and on the
    // marketing pages (HomePage -> useGuideLikes -> here) it was needlessly shipped
    // up front, hurting FCP/LCP. The subscription sets up a microtask later; for a
    // "something changed" invalidation signal that delay is imperceptible.
    let channel: RealtimeChannel | undefined
    let client: SupabaseClient | undefined
    let cancelled = false
    void import(`@/lib/supabase-browser`).then(({ supabase }) => {
      if (cancelled) return
      client = supabase
      // Channel name must be unique per (table, queryKey): Supabase keeps one
      // channel per topic and rejects adding postgres_changes callbacks to a
      // topic that's already subscribed. Two invalidations on the same table
      // (e.g. guide_likes -> public list + my likes) would otherwise collide.
      channel = supabase
        .channel(`rq-${table}-${key}`)
        .on(
          `postgres_changes`,
          { event: `*`, schema: `public`, table },
          () => { queryClient.invalidateQueries({ queryKey: [...queryKey] }) }
        )
        .subscribe()
    })

    return () => {
      cancelled = true
      if (client && channel) client.removeChannel(channel)
    }
  }, [table, key])
}
